import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { useOrders } from './useOrders';
import { OrderDetailDrawer } from './OrderDetailDrawer';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

const STATUSES = ['', 'pending', 'paid', 'provisioning', 'awaiting_withdrawal_period', 'cancelled', 'failed', 'refunded'];
const PROVIDERS = ['', 'stripe', 'paypal', 'sepa', 'manual'];

export function OrdersPage() {
  const { brandSlug, orderId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const query = useOrders({ search, status, provider });

  const rows = query.data?.orders ?? [];
  // Absolute Brand-Pfade — `../orders` mit `relative: 'path'` baute aus
  // /:brand/orders/:id den Pfad /:brand/orders/orders (cd .. && cd orders).
  // Drawer ließ sich dadurch nicht mehr schließen.
  const openDetail = (id) => navigate(`/${brandSlug}/orders/${id}`);
  const closeDetail = () => navigate(`/${brandSlug}/orders`);
  // Detail-Endpoint `/v1/admin/orders/:id` existiert (Stand 2026-06) nicht —
  // wir reichen die Row aus der List-Response direkt an den Drawer durch.
  const selectedOrder = orderId
    ? rows.find((r) => String(r.id) === String(orderId)) ?? null
    : null;

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="Brand-scoped · GET /v1/admin/orders"
      />
      <FilterBar
        search={search}
        searchPlaceholder="Order-Nr · Email · SKU…"
        onSearchChange={setSearch}
        onReset={
          search || status || provider
            ? () => {
                setSearch('');
                setStatus('');
                setProvider('');
              }
            : undefined
        }
        extras={
          <>
            <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 200 }}>
              {STATUSES.map((s) => (
                <option key={s || 'all'} value={s}>
                  {s || 'Alle Status'}
                </option>
              ))}
            </Input>
            <Input type="select" bsSize="sm" value={provider} onChange={(e) => setProvider(e.target.value)} style={{ maxWidth: 160 }}>
              {PROVIDERS.map((p) => (
                <option key={p || 'all'} value={p}>
                  {p || 'Alle Provider'}
                </option>
              ))}
            </Input>
          </>
        }
      />
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!rows.length}
        emptyMessage={search || status || provider ? 'Keine Treffer für aktuelle Filter.' : 'Noch keine Orders.'}
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 160 }}>Order-Nr</th>
            <th>Customer</th>
            <th>Items</th>
            <th style={{ width: 130 }}>Status</th>
            <th style={{ width: 100 }}>Provider</th>
            <th style={{ width: 110 }} className="text-end">Total</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 130 }}>Bezahlt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} role="button" onClick={() => openDetail(o.id)}>
              <td>
                <CopyChip value={o.orderNumber} label="Order-Nr." />
              </td>
              <td className="small">
                {o.userEmail ?? <span className="text-muted">—</span>}
                {o.userId != null && (
                  <div className="text-muted" style={{ fontSize: 11 }}>#{o.userId}</div>
                )}
              </td>
              <td>
                <span className="small text-muted">
                  {o.items.length === 0
                    ? '—'
                    : o.items.length === 1
                    ? <code>{o.items[0].sku ?? o.items[0].name ?? `Item ${o.items[0].id ?? ''}`}</code>
                    : `${o.items.length} Items`}
                </span>
              </td>
              <td>
                <StatusBadge value={o.status} />
              </td>
              <td>
                <code style={{ fontSize: 12 }}>{o.provider}</code>
              </td>
              <td className="text-end fw-semibold">
                {Number.isFinite(o.total) ? fmtEur.format(o.total / 100) : '—'}
              </td>
              <td>
                {o.createdAt ? <RelativeTime iso={o.createdAt} /> : <span className="text-muted">—</span>}
              </td>
              <td>
                {o.paidAt ? <RelativeTime iso={o.paidAt} /> : <span className="text-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <OrderDetailDrawer
        order={selectedOrder}
        isOpen={!!orderId}
        onClose={closeDetail}
      />
    </div>
  );
}
